import { t } from '@lingui/macro';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import oldType from '@helpers/oldType';
import { isAiSearchOn, semanticSearchFeature } from '@helpers/semantic_search';

import './styles/autocomplete.scss';
import { highlightSegments } from './suggestionHighlight';

// Screen readers announce the highlighted suggestion through aria-activedescendant on the
// search input, which needs a stable id per option to point at.
const optionId = (index) => `search-autocomplete-option-${index}`;

// Renders the part of a completion the user has not typed yet in bold, so the eye lands on
// what the suggestion is adding rather than on the characters they just pressed.
const phraseLabel = (label, query) => (
  highlightSegments(label, query).map((segment, index) => (
    segment.typed
      ? segment.text
      // eslint-disable-next-line react/no-array-index-key
      : <b key={index} className="search-autocomplete__completion">{segment.text}</b>
  ))
);

const Autocomplete = ({ suggestions, onSelect, onClose, loading, id, onActiveChange, query, context, phraseMode }) => {
  const selectedIndexRef = useRef(null);
  const setSelectedIndex = useState(null)[1];

  const openAssetModal = ({ assetKey, assetType, sectionKey, brandfolderSlug }) => {
    const url = `${window.location.protocol}//${window.location.host}/${brandfolderSlug}/sections/${sectionKey}/ui/${oldType(assetType)}/${assetKey}?edit=true`;
    BF.fullModal = true;
    BF.dialog.render(encodeURIComponent(assetKey), url, BF.handlerGroups.showAsset, null, 'assetModalTitle');
  };

  // The ref drives rendering; the state setter only exists to force one. Both are kept in
  // step here so the input's aria-activedescendant cannot drift from the visible highlight.
  const updateSelection = (index) => {
    selectedIndexRef.current = index;
    setSelectedIndex(index);
    onActiveChange(index === null ? null : optionId(index));
  };

  const onKeyDown = (event) => {
    if (!suggestions.length) { return undefined; }
    let index = selectedIndexRef.current;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (selectedIndexRef.current === null) {
          index = 0;
        } else {
          index = selectedIndexRef.current === suggestions.length - 1 ? 0 : selectedIndexRef.current + 1;
        }
        updateSelection(index);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (selectedIndexRef.current === null) {
          index = suggestions.length - 1;
        } else {
          index = selectedIndexRef.current === 0 ? suggestions.length - 1 : selectedIndexRef.current - 1;
        }
        updateSelection(index);
        break;
      case 'Tab': {
        if (phraseMode) {
          const tabIndex = selectedIndexRef.current ?? 0;
          const tabSelected = suggestions[tabIndex];
          if (tabSelected && tabSelected.type !== 'asset') {
            event.preventDefault();
            onSelect(tabSelected.value);
          }
        }
        break;
      }
      case 'Enter':
        if (suggestions[selectedIndexRef.current]) {
          const selected = suggestions[selectedIndexRef.current];
          if (selected.type === 'asset') {
            openAssetModal({
              assetKey: selected.asset_key,
              assetType: selected.asset_type,
              sectionKey: selected.section_key,
              brandfolderSlug: selected.brandfolder_slug,
            });
          } else {
            onSelect(selected.value);
          }
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }

    return undefined;
  };

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [suggestions]);

  if (!suggestions.length && !loading) { return null; }

  const suggestionEls = suggestions.map((suggestion, index) => {
    const {
      label, value, type,
      asset_key: assetKey, asset_type: assetType,
      section_key: sectionKey, thumbnail_url: thumbnailUrl,
      brandfolder_slug: brandfolderSlug,
    } = suggestion;
    const isAsset = type === 'asset';
    const isSelected = index === selectedIndexRef.current;

    const handleClick = () => {
      if (window.amplitudeService) {
        const now = Date.now();
        window.amplitudeService.trackEvent('Autocomplete Search', {
          'event group': 'Search',
          'key properties': {
            userId: window.BFG?.currentUser?.user_id,
            userRole: window.BFG?.currentUser?.role,
            orgSlugId: window.BFG?.org_slug,
            orgName: window.BFG?.org_name,
            nlsEnabled: isAiSearchOn(),
            eapEnabled: semanticSearchFeature(),
            brandfolderName: window.BFG?.resource?.name,
            pageUrl: window.location.href,
            timestamp: now,
            dateTime: new Date(now).toLocaleString('en-GB').replace(',', ''),
            searchTerm: query,
            autocompleteSelected: label,
            autocompletePosition: index,
            autocompleteListCount: suggestions.length,
            autocompleteList: suggestions.map((s, i) => ({ position: i, term: s.label })),
          },
        });
      }
      if (isAsset) {
        openAssetModal({ assetKey, assetType, sectionKey, brandfolderSlug });
      } else {
        onSelect(value);
      }
    };

    const liClass = [isAsset ? 'asset-suggestion-item' : null, isSelected ? 'selected' : null].filter(Boolean).join(' ') || null;

    // Only phrase completions get the emphasis. A structured label like `tag: "lucerne"` is
    // assembled from a field name, the committed search prefix and trailing spaces, so
    // marking it up would emphasise punctuation rather than anything the user typed.
    const labelContent = type === 'phrase' ? phraseLabel(label, query) : label;
    const showContext = type === 'phrase' && context.trim().length > 0;

    return (
      // A listbox may only own options, and an option may not contain a control, so the
      // role sits on the button and the li is presentational.
      <li
        key={isAsset ? assetKey : label}
        className={liClass}
        role="presentation"
      >
        <button
          aria-selected={isSelected}
          id={optionId(index)}
          onClick={handleClick}
          onFocus={() => updateSelection(index)}
          onMouseOver={() => updateSelection(index)}
          role="option"
          type="button"
        >
          {isAsset ? (
            <span className="asset-suggestion">
              <img alt={label} className="asset-suggestion__thumbnail" src={thumbnailUrl} />
              <span className="asset-suggestion__name">{label}</span>
            </span>
          ) : (
            <>
              {/* Suggestions complete the last fragment, so a bare "beach scene" reads as
                  though "man on" had been ignored. Showing the text it follows, dimmed, makes
                  the row the query it would search for. Only phrase completions need it: a
                  structured label already carries the committed prefix. */}
              {showContext && <span className="search-autocomplete__context">{context}</span>}
              {labelContent}
            </>
          )}
        </button>
      </li>
    );
  });

  return (
    <ul
      aria-label={t`Search suggestions`}
      className="search-autocomplete"
      id={id}
      onMouseLeave={() => updateSelection(null)}
      role="listbox"
    >
      {loading ? (
        <li className="autocomplete-loading" role="presentation">
          <span className="autocomplete-loading__dot" />
          <span className="autocomplete-loading__dot" />
          <span className="autocomplete-loading__dot" />
        </li>
      ) : suggestionEls}
    </ul>
  );
};

Autocomplete.propTypes = {
  suggestions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    type: PropTypes.string,
    asset_key: PropTypes.string,
    asset_type: PropTypes.string,
    section_key: PropTypes.string,
    thumbnail_url: PropTypes.string,
    brandfolder_slug: PropTypes.string,
  })),
  onSelect: PropTypes.func,
  onClose: PropTypes.func,
  loading: PropTypes.bool,
  id: PropTypes.string,
  onActiveChange: PropTypes.func,
  phraseMode: PropTypes.bool,
  query: PropTypes.string,
  context: PropTypes.string,
};

Autocomplete.defaultProps = {
  context: '',
  id: undefined,
  loading: false,
  onActiveChange: () => {},
  onClose: () => {},
  onSelect: () => {},
  phraseMode: false,
  query: '',
  suggestions: [],
};

export default Autocomplete;
